import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Typography, Modal } from '@ag1/ui';
import { type UnifiedButton } from 'types/studio/studio';
import { Direction, useOnSwipe } from 'shared/hooks';
import { findIndexWrapAround } from 'shared/utils/findIndexWrapAround';
import { CustomPortableText } from 'shared/studio/PortableText';
import { StudioBaseImageV2 } from 'shared/studio/StudioBaseImage/StudioBaseImage';
import { CustomModalArrows } from './CustomModalArrows';
import * as styles from './CustomModal.css';

type SelectedSlide = Pick<
  Extract<UnifiedButton['customModalItems'], readonly unknown[]>[number],
  | 'superHeadline'
  | 'modalImage'
  | 'headline'
  | 'subtitle'
  | 'bodyHeadline'
  | 'bodyText'
  | 'listTitle'
  | 'list'
  | 'label1'
  | 'data1'
  | 'label2'
  | 'data2'
>;

type CustomModalProps = {
  isOpen: boolean;
  onClose: () => void;
  customModalItems: UnifiedButton['customModalItems'];
};

export const CustomModal = ({ isOpen, onClose, customModalItems }: CustomModalProps) => {
  const [selected, setSelected] = useState<SelectedSlide | null>(
    customModalItems && customModalItems.length > 0 ? customModalItems[0] : null
  );
  const [fadeInOut, setFadeInOut] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const index = customModalItems
    ? customModalItems.findIndex((item: SelectedSlide) => item === selected)
    : 0;
  const hasSliderArrows = customModalItems && customModalItems.length > 1;

  const handleArrowClick = (offset: number) => {
    if (customModalItems) {
      const newIndex = findIndexWrapAround(index, customModalItems.length, offset);
      setFadeInOut(true);
      setImageLoaded(false);
      setTimeout(() => {
        setSelected(customModalItems[newIndex]);
        setFadeInOut(false);
      }, 150);
    }
  };

  const touchEvents = useOnSwipe(Direction.horizontal, {
    onSwipeLeft: () => {
      handleArrowClick(+1);
    },
    onSwipeRight: () => {
      handleArrowClick(-1);
    }
  });

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  useEffect(() => {
    const accessibilityElement = document.getElementById('usntA40Toggle');
    if (accessibilityElement) accessibilityElement.style.zIndex = '99';
  }, []);

  if (!selected) return null;

  const {
    superHeadline,
    modalImage,
    headline,
    subtitle,
    bodyHeadline,
    bodyText,
    listTitle,
    list,
    label1,
    data1,
    label2,
    data2
  } = selected;

  const hasData = (label1 && data1) || (label2 && data2);

  return (
    <Modal animation="slide" isOpen={isOpen} onClose={onClose}>
      <Modal.Header className={styles.header}>
        {superHeadline && (
          <Typography
            component="p"
            variant="bodyMono"
            size="1"
            noPadding
            className={clsx({ [styles.fadeInOut]: fadeInOut }, styles.superHeadline)}>
            {superHeadline}
          </Typography>
        )}
      </Modal.Header>

      <Modal.Content className={clsx({ [styles.fadeInOut]: fadeInOut }, styles.content)}>
        {modalImage?.image && (
          <div className={styles.imageWrapper} {...touchEvents}>
            {!imageLoaded && <div className={styles.skeleton} />}
            <StudioBaseImageV2
              className={clsx(styles.image, { [styles.imageLoaded]: imageLoaded })}
              image={modalImage.image}
              mobileImage={modalImage.mobileImage}
              withMobileImage={modalImage.withMobileImage}
              tabletImage={modalImage.tabletImage}
              eagerLoading
              onLoad={handleImageLoad}
            />
          </div>
        )}
        <div className={styles.textContent}>
          {headline && (
            <Typography
              component="h1"
              variant="headlineSans"
              size="3"
              noPadding
              className={clsx(styles.headline, {
                [styles.headlineHasImageAbove]: modalImage?.image != null
              })}>
              {headline}
            </Typography>
          )}
          {subtitle && (
            <Typography
              component="p"
              variant="subtitle"
              size="1"
              noPadding
              className={styles.subtitle}>
              {subtitle}
            </Typography>
          )}
          {bodyHeadline && (
            <Typography
              component="h2"
              variant="body"
              size="3"
              noPadding
              className={styles.bodyHeadline}>
              {bodyHeadline}
            </Typography>
          )}
          {bodyText && (
            <Typography variant="body" size="1" noPadding className={styles.bodyText}>
              <CustomPortableText value={bodyText} />
            </Typography>
          )}
          {listTitle && (
            <Typography
              component="h3"
              variant="subtitleMono"
              size="2"
              noPadding
              className={styles.listTitle}>
              {listTitle}
            </Typography>
          )}
          {list && list.length > 0 && (
            <ul className={styles.list}>
              {list.map((item: string) => (
                <li key={item} className={styles.listItem}>
                  <Typography
                    component="p"
                    variant="body"
                    size="1"
                    noPadding
                    className={styles.listItemText}>
                    {item}
                  </Typography>
                </li>
              ))}
            </ul>
          )}
          {hasData && (
            <div className={styles.dataWrapper}>
              <div>
                {label1 && (
                  <Typography
                    component="p"
                    variant="captionMono"
                    size="2"
                    noPadding
                    className={styles.label}>
                    {label1}
                  </Typography>
                )}
                {data1 && (
                  <Typography
                    component="p"
                    variant="body"
                    size="3"
                    noPadding
                    className={styles.data}>
                    {data1}
                  </Typography>
                )}
              </div>
              <div>
                {label2 && (
                  <Typography
                    component="p"
                    variant="captionMono"
                    size="2"
                    noPadding
                    className={styles.label}>
                    {label2}
                  </Typography>
                )}
                {data2 && (
                  <Typography
                    component="p"
                    variant="body"
                    size="3"
                    noPadding
                    className={styles.data}>
                    {data2}
                  </Typography>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal.Content>

      {hasSliderArrows && (
        <Modal.Footer className={styles.footer}>
          <CustomModalArrows
            onClickPrev={() => handleArrowClick(-1)}
            onClickNext={() => handleArrowClick(1)}
          />
          <Typography
            component="p"
            variant="body"
            size="1"
            noPadding
            className={clsx({ [styles.fadeInOut]: fadeInOut }, styles.cardsLengthText)}>
            <span>{index + 1}</span> of {customModalItems && customModalItems.length}
          </Typography>
        </Modal.Footer>
      )}
    </Modal>
  );
};
